// frontend/src/services/api.js
import axios from 'axios';

// Get API URL from environment variable or use default
const API_URL = process.env.REACT_APP_API_URL || '';

// Create axios instance with base configuration
export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for cookies/sessions
});

// ─── CSRF Helper ─────────────────────────────────────────────────────────────

function getCsrfToken() {
  const value = `; ${document.cookie}`;
  const parts = value.split('; XSRF-TOKEN=');
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// Fetch initial CSRF token on app load
export const initCsrfToken = async () => {
  try {
    await api.get('/api/auth/csrf-token');
  } catch (error) {
    console.warn('Failed to fetch CSRF token:', error.message);
  }
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Get auth token from localStorage
    const token = localStorage.getItem('token');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

   // Attach CSRF token for state-changing requests
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      config.headers['X-XSRF-TOKEN'] = csrfToken;
    } 

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle CSRF token errors — refresh and retry once
    if (
      error.response?.status === 403 &&
      error.response?.data?.error?.includes('CSRF') &&
      !originalRequest._csrfRetry
    ) {
      originalRequest._csrfRetry = true;
      try {
        await api.get('/api/auth/csrf-token');
        const newToken = getCsrfToken();
        if (newToken) {
          originalRequest.headers['X-XSRF-TOKEN'] = newToken;
        }
        return api(originalRequest);
      } catch (csrfError) {
        console.error('CSRF token refresh failed:', csrfError.message);
      }
    }

    // Handle auth errors
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (!window.location.pathname.includes('/auth')) {
        window.location.href = '/auth/login';
      }
    }

    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  login: async (credentials) => {
    const response = await api.post('/api/auth/login', credentials);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },
  
  register: async (userData) => {
    const response = await api.post('/api/auth/register', userData);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },
  
  logout: async () => {
    try {
      await api.post('/api/auth/logout');
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/auth/login';
    }
  },
  
  getCurrentUser: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  }
};

// Question API calls
export const questionAPI = {
  getQuestions: async (params) => {
    const response = await api.get('/api/questions', { params });
    return response.data;
  },
  
  getQuestion: async (id) => {
    const response = await api.get(`/api/questions/${id}`);
    return response.data;
  },
  
  submitAnswer: async (id, answer) => {
    const response = await api.post(`/api/questions/${id}/answer`, { answer });
    return response.data;
  },
  
  bookmarkQuestion: async (id) => {
    const response = await api.post(`/api/questions/${id}/bookmark`);
    return response.data;
  }
};

// Post API calls
export const postAPI = {
  getPosts: async (params) => {
    const response = await api.get('/api/posts', { params });
    return response.data;
  },
  
  createPost: async (postData) => {
    const response = await api.post('/api/posts', postData);
    return response.data;
  },
  
  likePost: async (id) => {
    const response = await api.post(`/api/posts/${id}/like`);
    return response.data;
  },
  
  commentOnPost: async (id, comment) => {
    const response = await api.post(`/api/posts/${id}/comments`, { content: comment });
    return response.data;
  }
};

// User API calls
export const userAPI = {
  getProfile: async (username) => {
    const response = await api.get(`/api/users/${username}`);
    return response.data;
  },
  
  updateProfile: async (updates) => {
    const response = await api.put('/api/users/profile', updates);
    return response.data;
  },
  
  followUser: async (userId) => {
    const response = await api.post(`/api/users/${userId}/follow`);
    return response.data;
  },
  
  unfollowUser: async (userId) => {
    const response = await api.post(`/api/users/${userId}/unfollow`);
    return response.data;
  }
};

// Progress API calls
export const progressAPI = {
  getUserProgress: async () => {
    const response = await api.get('/api/progress/me');
    return response.data;
  },
  
  getTopicProgress: async (topic) => {
    const response = await api.get(`/api/progress/topic/${topic}`);
    return response.data;
  }
};

// Community API calls
export const communityAPI = {
  getCommunities: async () => {
    const response = await api.get('/api/communities');
    return response.data;
  },
  
  joinCommunity: async (id) => {
    const response = await api.post(`/api/communities/${id}/join`);
    return response.data;
  },
  
  leaveCommunity: async (id) => {
    const response = await api.post(`/api/communities/${id}/leave`);
    return response.data;
  }
};

// Search API calls
export const searchAPI = {
  search: async (query, type = 'all') => {
    const response = await api.get('/api/search', { 
      params: { q: query, type } 
    });
    return response.data;
  }
};

// Export default api instance
export default api;
